import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';

const links = [
  { href: '#inicio', label: 'Início' },
  { href: '#preinicio', label: 'Para começar' },
  { href: '#amostras', label: 'Conteúdos' },
  { href: '#dicas', label: 'Dicas' },
  { href: '#curiosidades', label: 'Curiosidades' },
  { href: '#historia', label: 'História' },
  { href: '#metodo', label: 'Método' },
  { href: '#evolucao', label: 'Evolução' },
  { href: '#ajuda', label: 'Ajuda' },
];

export default function PublicHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.accessLevel === 'admin';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const studentHref = user ? '/aulas' : '/entrar?next=%2Faulas';

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'border-b border-orange-100 bg-white/96 shadow-sm backdrop-blur-xl'
          : 'border-b border-orange-100/70 bg-white/95 backdrop-blur-xl'
      }`}
    >
      <div className="mx-auto flex h-[64px] max-w-[1480px] items-center justify-between px-4 sm:px-6 lg:h-[76px] lg:px-8">
        <a href="#inicio" className="flex items-center gap-2.5">
          <span
            className={`flex h-10 w-10 items-center justify-center rounded-xl ${
              scrolled ? 'bg-orange-500 text-white' : 'bg-orange-500/95 text-white'
            }`}
          >
            <i className="ri-music-2-fill text-2xl" />
          </span>
          <div className="leading-none">
            <strong
              className={`block text-[18px] font-black tracking-[-0.02em] sm:text-[21px] ${
                scrolled ? 'text-slate-950' : 'text-slate-950'
              }`}
            >
              PARTITURAS
            </strong>
            <span
              className={`mt-1 block text-[8px] font-extrabold uppercase tracking-[0.18em] ${
                scrolled ? 'text-orange-600' : 'text-orange-600'
              }`}
            >
              pratique até dominar
            </span>
          </div>
        </a>

        <nav className="hidden items-center gap-1 xl:flex" aria-label="Navegação pública">
          {links.map((link) => (
            <a
              key={link.label}
              href={link.href}
              className={`rounded-full px-3 py-2 text-[13px] font-bold transition ${
                scrolled
                  ? 'text-slate-700 hover:bg-orange-50 hover:text-orange-600'
                  : 'text-slate-700 hover:bg-orange-50 hover:text-orange-600'
              }`}
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-2 xl:flex">
          <Link
            to={studentHref}
            className={`rounded-full px-4 py-2.5 text-sm font-extrabold transition ${
              scrolled
                ? 'text-slate-700 hover:bg-orange-50 hover:text-orange-600'
                : 'text-slate-700 hover:bg-orange-50 hover:text-orange-600'
            }`}
          >
            {user ? 'Área do aluno' : 'Entrar'}
          </Link>
          {isAdmin && (
            <Link
              to="/admin"
              className="inline-flex items-center gap-2 rounded-full border border-orange-300 bg-orange-50 px-4 py-2.5 text-sm font-extrabold text-orange-800 hover:bg-orange-100"
            >
              <i className="ri-shield-user-line text-lg" />
              Administração
            </Link>
          )}
          {!user ? (
            <Link
              to="/criar-conta?next=%2Faulas%2F1"
              className="rounded-full bg-orange-500 px-5 py-2.5 text-sm font-extrabold text-white shadow-lg shadow-orange-950/20 transition hover:bg-orange-600"
            >
              Criar conta
            </Link>
          ) : null}
        </div>

        <button
          type="button"
          aria-label="Abrir menu"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
          className={`flex h-10 w-10 items-center justify-center rounded-xl xl:hidden ${
            scrolled ? 'text-slate-900 hover:bg-orange-50' : 'text-slate-900 hover:bg-orange-50'
          }`}
        >
          <i className={`${open ? 'ri-close-line' : 'ri-menu-line'} text-2xl`} />
        </button>
      </div>

      {open ? (
        <div className="border-t border-orange-100 bg-white px-4 py-4 shadow-lg xl:hidden">
          <nav className="mx-auto flex max-w-[1480px] flex-col gap-1">
            {links.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setOpen(false)}
                className="rounded-xl px-3 py-3 text-sm font-bold text-slate-700 hover:bg-orange-50 hover:text-orange-600"
              >
                {link.label}
              </a>
            ))}
            <div className="mt-2 grid grid-cols-2 gap-2">
              <Link
                to={studentHref}
                onClick={() => setOpen(false)}
                className="rounded-full border border-orange-200 px-4 py-3 text-center text-sm font-extrabold text-orange-700"
              >
                {user ? 'Área do aluno' : 'Entrar'}
              </Link>
              {!user ? (
                <Link
                  to="/criar-conta?next=%2Faulas%2F1"
                  onClick={() => setOpen(false)}
                  className="rounded-full bg-orange-500 px-4 py-3 text-center text-sm font-extrabold text-white"
                >
                  Criar conta
                </Link>
              ) : (
                <Link
                  to="/aulas/1"
                  onClick={() => setOpen(false)}
                  className="rounded-full bg-orange-500 px-4 py-3 text-center text-sm font-extrabold text-white"
                >
                  Continuar
                </Link>
              )}
            </div>
            {isAdmin && (
              <Link
                to="/admin"
                onClick={() => setOpen(false)}
                className="mt-3 flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-3 text-sm font-extrabold text-white"
              >
                <i className="ri-shield-user-line text-lg" />
                Minha conta · Administração
              </Link>
            )}
          </nav>
        </div>
      ) : null}
    </header>
  );
}
